import { useCallback, useContext, useMemo, useState } from "react";
import AuthContext from "../../context/AuthProvider";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { ASTIKA_ACTIONS } from "../../features/cases/listActions";
import runWithConcurrency from "../../features/cases/runWithConcurrency";

export const RECHECK_CAP = 25;

const isFav = (c) => c?.fav === 1 || c?.fav === true;
const isRateLimited = (err) => err?.response?.status === 429;

const DEFAULT_NOUN = { one: "υπόθεση", many: "υποθέσεις", this: "αυτή την υπόθεση" };

// Single and bulk case actions for the desktop page, with optimistic list
// updates. Bulk actions loop over the single-case endpoints with low
// concurrency (the API has no bulk-by-ids endpoint yet). `actions` is one of
// the sets in features/cases/listActions; `caps` reports which it supports.
export default function useCaseMutations({
  allCases,
  updateCase,
  removeCase,
  notify,
  actions = ASTIKA_ACTIONS,
  noun = DEFAULT_NOUN,
}) {
  const { setFav, recheck, deleteCase, saveNote: saveNoteAction, startBulkCheckAll } = actions;
  const axiosPrivate = useAxiosPrivate();
  const { checking, setChecking } = useContext(AuthContext);
  const [progress, setProgress] = useState(null); // { label, done, total }
  const [bulkChecking, setBulkChecking] = useState(false);

  const findCase = useCallback(
    (id) => allCases.find((c) => String(c.id) === String(id)),
    [allCases]
  );

  const markChecking = useCallback(
    (ids) => {
      const current = Array.isArray(checking) ? checking : [];
      const existing = new Set(current.map((c) => String(c.case_id)));
      setChecking([
        ...current,
        ...ids
          .filter((id) => !existing.has(String(id)))
          .map((id) => ({ case_id: id, category: actions.checkCategory })),
      ]);
    },
    [checking, setChecking, actions.checkCategory]
  );

  const toggleFav = useCallback(
    async (id) => {
      const target = findCase(id);
      if (!target) return;
      const next = !isFav(target);
      updateCase(id, { fav: next ? 1 : 0 });
      try {
        await setFav(axiosPrivate, id, next);
      } catch (err) {
        console.error(err);
        updateCase(id, { fav: next ? 0 : 1 });
        notify("Δεν ήταν δυνατή η αλλαγή στα σημαντικά.", "error");
      }
    },
    [axiosPrivate, findCase, updateCase, notify, setFav]
  );

  const recheckOne = useCallback(
    async (id) => {
      markChecking([id]);
      try {
        await recheck(axiosPrivate, id);
      } catch (err) {
        console.error(err);
        notify(
          isRateLimited(err)
            ? "Πολλές αιτήσεις ελέγχου. Δοκιμάστε ξανά σε λίγο."
            : "Αποτυχία εκκίνησης ελέγχου.",
          "error"
        );
      }
    },
    [axiosPrivate, markChecking, notify, recheck]
  );

  // Returns true when the case was deleted.
  const deleteOne = useCallback(
    async (id) => {
      if (!window.confirm(`Είστε σίγουρος ότι θέλετε να διαγράψετε ${noun.this};`)) {
        return false;
      }
      try {
        await deleteCase(axiosPrivate, id);
        removeCase(id);
        return true;
      } catch (err) {
        console.error(err);
        notify("Αποτυχία διαγραφής.", "error");
        return false;
      }
    },
    [axiosPrivate, removeCase, notify, deleteCase, noun]
  );

  // Optimistic; restores the previous note when the request fails.
  const saveNote = useCallback(
    async (id, note) => {
      const previous = findCase(id)?.note ?? "";
      if (previous === note) return true;
      updateCase(id, { note });
      try {
        await saveNoteAction(axiosPrivate, id, note);
        return true;
      } catch (err) {
        console.error(err);
        updateCase(id, { note: previous });
        notify("Δεν ήταν δυνατή η αποθήκευση της σημείωσης.", "error");
        return false;
      }
    },
    [axiosPrivate, findCase, updateCase, notify, saveNoteAction]
  );

  const runBulk = useCallback(
    async (label, ids, limit, fn, options = {}) => {
      setProgress({ label, done: 0, total: ids.length });
      try {
        return await runWithConcurrency(ids, limit, fn, {
          ...options,
          onProgress: (done, total) => setProgress({ label, done, total }),
        });
      } finally {
        setProgress(null);
      }
    },
    []
  );

  // Stars all when any is unstarred, otherwise unstars all.
  const bulkFav = useCallback(
    async (ids) => {
      const cases = ids.map(findCase).filter(Boolean);
      const next = !cases.every(isFav);
      const changed = cases.filter((c) => isFav(c) !== next).map((c) => c.id);
      changed.forEach((id) => updateCase(id, { fav: next ? 1 : 0 }));
      const { failed } = await runBulk(
        next ? "Σήμανση" : "Αφαίρεση σήμανσης",
        changed,
        4,
        (id) => setFav(axiosPrivate, id, next)
      );
      failed.forEach(({ item }) => updateCase(item, { fav: next ? 0 : 1 }));
      if (failed.length) {
        notify(`Απέτυχαν ${failed.length} από ${changed.length} αλλαγές.`, "error");
      }
    },
    [axiosPrivate, findCase, updateCase, runBulk, notify, setFav]
  );

  const bulkDelete = useCallback(
    async (ids) => {
      if (
        !window.confirm(
          ids.length === 1
            ? `Είστε σίγουρος ότι θέλετε να διαγράψετε ${noun.this};`
            : `Είστε σίγουρος ότι θέλετε να διαγράψετε ${ids.length} ${noun.many};`
        )
      ) {
        return [];
      }
      const { ok, failed } = await runBulk("Διαγραφή", ids, 3, async (id) => {
        await deleteCase(axiosPrivate, id);
        removeCase(id);
      });
      notify(
        failed.length
          ? `Διαγράφηκαν ${ok.length}. Απέτυχαν ${failed.length}.`
          : `Διαγράφηκαν ${ok.length} ${ok.length === 1 ? noun.one : noun.many}.`,
        failed.length ? "error" : "ok"
      );
      return ok;
    },
    [axiosPrivate, removeCase, runBulk, notify, deleteCase, noun]
  );

  const bulkRecheck = useCallback(
    async (ids) => {
      let targets = ids;
      if (ids.length > RECHECK_CAP) {
        if (
          !window.confirm(
            `Μπορούν να ελεγχθούν έως ${RECHECK_CAP} υποθέσεις τη φορά. Να ελεγχθούν οι πρώτες ${RECHECK_CAP};`
          )
        ) {
          return;
        }
        targets = ids.slice(0, RECHECK_CAP);
      }
      markChecking(targets);
      const { ok, failed, skipped } = await runBulk(
        "Έλεγχος",
        targets,
        2,
        (id) => recheck(axiosPrivate, id),
        { delayMs: 300, shouldStop: isRateLimited }
      );
      const rateLimited = failed.some(({ err }) => isRateLimited(err));
      if (rateLimited) {
        notify(
          `Το όριο αιτήσεων ελέγχου εξαντλήθηκε. Ξεκίνησαν ${ok.length} από ${targets.length}.`,
          "error"
        );
      } else if (failed.length || skipped.length) {
        notify(`Ξεκίνησαν ${ok.length} έλεγχοι. Απέτυχαν ${failed.length}.`, "error");
      } else {
        notify(`Ξεκίνησε ο έλεγχος ${ok.length} υποθέσεων.`, "ok");
      }
    },
    [axiosPrivate, markChecking, runBulk, notify, recheck]
  );

  const checkAll = useCallback(async () => {
    if (bulkChecking || !startBulkCheckAll) return;
    setBulkChecking(true);
    const { ok, message } = await startBulkCheckAll(axiosPrivate);
    setBulkChecking(false);
    notify(message, ok ? "ok" : "error");
  }, [axiosPrivate, bulkChecking, notify, startBulkCheckAll]);

  const caps = useMemo(
    () => ({
      fav: Boolean(setFav),
      recheck: Boolean(recheck),
      checkAll: Boolean(startBulkCheckAll),
      note: Boolean(saveNoteAction),
    }),
    [setFav, recheck, startBulkCheckAll, saveNoteAction]
  );

  return {
    caps,
    progress,
    bulkChecking,
    toggleFav,
    recheckOne,
    deleteOne,
    saveNote,
    bulkFav,
    bulkDelete,
    bulkRecheck,
    checkAll,
  };
}
